import { CommonModule } from "@angular/common";
import {
  AfterViewInit,
  Component,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import {
  AnnouncementEffectIntensity,
  AnnouncementEffects,
  AnnouncementEffectType,
} from "../../services/announcement/announcement.models";

/** One animated shape. Fields are only meaningful for the effect that spawned it. */
interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  color: string;
  /** Remaining life in seconds; <= 0 removes the particle. Infinity for ambient effects. */
  life: number;
  maxLife: number;
  rotation: number;
  rotationSpeed: number;
  /** Phase offset for sway/twinkle so particles never move in lockstep. */
  phase: number;
  /** Fireworks only: a rocket spawns sparks when it dies. */
  isRocket?: boolean;
}

/** How many particles a NORMAL run of each effect uses on a reference-sized (560×420) panel. */
const BASE_COUNT: Record<AnnouncementEffectType, number> = {
  CONFETTI: 110,
  SNOW: 70,
  SPARKLES: 26,
  FIREWORKS: 3, // rockets per volley — each explodes into ~70 sparks
  BUBBLES: 34,
};

const INTENSITY_SCALE: Record<AnnouncementEffectIntensity, number> = {
  SUBTLE: 0.45,
  NORMAL: 1,
  FESTIVE: 1.8,
};

/** Effects that play once on open (vs. ambient ones that run while the popup is up). */
const BURST_EFFECTS: ReadonlySet<AnnouncementEffectType> = new Set(["CONFETTI", "FIREWORKS"]);

/**
 * Decorative particle overlay for announcement popups — confetti, snow, sparkles, fireworks
 * or bubbles, configured per announcement from the admin form (Effects step).
 *
 * Hand-rolled on a single <canvas> rather than pulling in tsparticles/canvas-confetti: the five
 * presets need a few hundred lines, no dependency, and one rAF loop that stops by itself.
 * Deliberate behaviours:
 *  - pointer-events: none — it can never intercept a click meant for the popup;
 *  - `prefers-reduced-motion` renders nothing at all;
 *  - burst effects (confetti, fireworks) play once and the loop then stops — except in the
 *    admin simulator (`loop`), where they replay so the admin always sees what they picked;
 *  - ambient effects (snow, sparkles, bubbles) run while the popup is open, sized to stay
 *    subtle rather than carnival.
 */
@Component({
  selector: "app-announcement-particles",
  standalone: true,
  imports: [CommonModule],
  template: `<canvas #canvas aria-hidden="true"></canvas>`,
  styles: [
    `
      :host {
        position: absolute;
        inset: 0;
        display: block;
        pointer-events: none;
        overflow: hidden;
        border-radius: inherit;
      }
      canvas {
        width: 100%;
        height: 100%;
        display: block;
      }
    `,
  ],
})
export class AnnouncementParticlesComponent implements AfterViewInit, OnChanges, OnDestroy {
  @Input({ required: true }) effects!: AnnouncementEffects;
  /** Theme accent — the seed of the default palette when no custom colours are set. */
  @Input() accent: string | null = "#4f7cff";
  /** Replays burst effects forever — used by the admin preview simulator. */
  @Input() loop = false;

  @ViewChild("canvas") private canvasRef!: ElementRef<HTMLCanvasElement>;

  private particles: Particle[] = [];
  private rafId: number | null = null;
  private lastTime = 0;
  /** Seconds until the next volley/replay of a burst effect. */
  private replayIn = 0;
  private resizeObserver?: ResizeObserver;
  private viewReady = false;

  constructor(
    private host: ElementRef<HTMLElement>,
    private zone: NgZone,
  ) {}

  ngAfterViewInit(): void {
    this.viewReady = true;
    this.resizeObserver = new ResizeObserver(() => this.fitCanvas());
    this.resizeObserver.observe(this.host.nativeElement);
    this.fitCanvas();
    this.restart();
  }

  ngOnChanges(changes: SimpleChanges): void {
    // The simulator rebuilds the announcement on every keystroke — only an actual change of
    // effect settings should reset the animation, not every preview refresh.
    const effects = changes["effects"];
    if (
      this.viewReady &&
      effects &&
      JSON.stringify(effects.previousValue) !== JSON.stringify(effects.currentValue)
    ) {
      this.restart();
    }
  }

  ngOnDestroy(): void {
    this.stop();
    this.resizeObserver?.disconnect();
  }

  // ── Setup ─────────────────────────────────────────────────────────────────

  private get effectType(): AnnouncementEffectType {
    return this.effects?.type ?? "CONFETTI";
  }

  private get isAmbient(): boolean {
    return !BURST_EFFECTS.has(this.effectType);
  }

  private get count(): number {
    const scale = INTENSITY_SCALE[this.effects?.intensity ?? "NORMAL"] ?? 1;
    const { width, height } = this.size();
    // Scale with panel area so the embedded simulator isn't buried under a full-size storm.
    const area = Math.max(0.35, Math.min(1.6, (width * height) / (560 * 420)));
    return Math.max(4, Math.round(BASE_COUNT[this.effectType] * scale * area));
  }

  private get palette(): string[] {
    const custom = (this.effects?.colors ?? []).filter((c) => !!c && !!c.trim());
    if (custom.length) {
      return custom;
    }
    // Default: the announcement's own accent plus festive companions that read well on
    // both dark and light panels.
    return [this.accent || "#4f7cff", "#22c55e", "#f59e0b", "#ec4899", "#38bdf8", "#ffffff"];
  }

  private prefersReducedMotion(): boolean {
    return typeof window.matchMedia === "function" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  private size(): { width: number; height: number } {
    const rect = this.host.nativeElement.getBoundingClientRect();
    return { width: Math.max(1, rect.width), height: Math.max(1, rect.height) };
  }

  private fitCanvas(): void {
    if (!this.canvasRef) {
      return;
    }
    const canvas = this.canvasRef.nativeElement;
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const { width, height } = this.size();
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);
    canvas.getContext("2d")?.setTransform(dpr, 0, 0, dpr, 0, 0);
  }

  private restart(): void {
    this.stop();
    if (!this.effects?.enabled || this.prefersReducedMotion()) {
      this.clear();
      return;
    }
    this.particles = [];
    this.spawnInitial();
    this.replayIn = 0;
    this.lastTime = 0;
    // Outside Angular: 60 rAF ticks/second must not run change detection.
    this.zone.runOutsideAngular(() => {
      this.rafId = requestAnimationFrame((t) => this.tick(t));
    });
  }

  private stop(): void {
    if (this.rafId !== null) {
      cancelAnimationFrame(this.rafId);
      this.rafId = null;
    }
  }

  private clear(): void {
    const canvas = this.canvasRef?.nativeElement;
    canvas?.getContext("2d")?.clearRect(0, 0, canvas.width, canvas.height);
  }

  // ── Spawning ──────────────────────────────────────────────────────────────

  private pick(): string {
    const palette = this.palette;
    return palette[Math.floor(Math.random() * palette.length)];
  }

  private spawnInitial(): void {
    const { width, height } = this.size();
    switch (this.effectType) {
      case "CONFETTI":
        this.spawnConfetti(width);
        break;
      case "FIREWORKS":
        this.spawnRockets(width, height);
        break;
      case "SNOW":
        for (let i = 0; i < this.count; i++) this.particles.push(this.makeSnow(width, height, true));
        break;
      case "SPARKLES":
        for (let i = 0; i < this.count; i++) this.particles.push(this.makeSparkle(width, height));
        break;
      case "BUBBLES":
        for (let i = 0; i < this.count; i++) this.particles.push(this.makeBubble(width, height, true));
        break;
    }
  }

  /** Two angled cannons at the top corners plus a light rain across the top edge. */
  private spawnConfetti(width: number): void {
    const total = this.count;
    for (let i = 0; i < total; i++) {
      const fromCannon = i < total * 0.7;
      const left = i % 2 === 0;
      // Elevation 20°–70° above horizontal; the right cannon mirrors the left one.
      const elevation = (20 + Math.random() * 50) * (Math.PI / 180);
      const speed = fromCannon ? 260 + Math.random() * 320 : 40 + Math.random() * 60;
      this.particles.push({
        x: fromCannon ? (left ? -8 : width + 8) : Math.random() * width,
        y: fromCannon ? 40 + Math.random() * 60 : -10 - Math.random() * 40,
        vx: fromCannon ? Math.cos(elevation) * speed * (left ? 1 : -1) : (Math.random() - 0.5) * 30,
        vy: fromCannon ? -Math.sin(elevation) * speed * 0.9 : speed,
        size: 5 + Math.random() * 5,
        color: this.pick(),
        life: 2.6 + Math.random() * 1.6,
        maxLife: 4,
        rotation: Math.random() * Math.PI * 2,
        rotationSpeed: (Math.random() - 0.5) * 12,
        phase: Math.random() * Math.PI * 2,
      });
    }
  }

  private spawnRockets(width: number, height: number): void {
    const rockets = this.count;
    for (let i = 0; i < rockets; i++) {
      this.particles.push({
        x: width * (0.2 + Math.random() * 0.6),
        y: height + 6,
        vx: (Math.random() - 0.5) * 50,
        vy: -(height * (0.9 + Math.random() * 0.5)),
        size: 2.4,
        color: this.pick(),
        life: 0.55 + Math.random() * 0.35,
        maxLife: 0.9,
        rotation: 0,
        rotationSpeed: 0,
        phase: Math.random() * Math.PI * 2,
        isRocket: true,
      });
    }
  }

  private explode(rocket: Particle): Particle[] {
    const sparks: Particle[] = [];
    const sparkCount = 50 + Math.floor(Math.random() * 30);
    const color = this.pick();
    for (let i = 0; i < sparkCount; i++) {
      const angle = (i / sparkCount) * Math.PI * 2 + Math.random() * 0.2;
      const speed = 60 + Math.random() * 150;
      sparks.push({
        x: rocket.x,
        y: rocket.y,
        vx: Math.cos(angle) * speed,
        vy: Math.sin(angle) * speed,
        size: 1.6 + Math.random() * 1.8,
        color: Math.random() < 0.75 ? color : this.pick(),
        life: 0.8 + Math.random() * 0.9,
        maxLife: 1.7,
        rotation: 0,
        rotationSpeed: 0,
        phase: Math.random() * Math.PI * 2,
      });
    }
    return sparks;
  }

  private makeSnow(width: number, height: number, anywhere: boolean): Particle {
    return {
      x: Math.random() * width,
      y: anywhere ? Math.random() * height : -6,
      vx: 0,
      vy: 18 + Math.random() * 30,
      size: 1.4 + Math.random() * 2.4,
      color: this.pick(),
      life: Infinity,
      maxLife: Infinity,
      rotation: 0,
      rotationSpeed: 0,
      phase: Math.random() * Math.PI * 2,
    };
  }

  private makeSparkle(width: number, height: number): Particle {
    const maxLife = 1.2 + Math.random() * 1.8;
    return {
      x: Math.random() * width,
      y: Math.random() * height,
      vx: (Math.random() - 0.5) * 8,
      vy: (Math.random() - 0.5) * 8,
      size: 2.5 + Math.random() * 4,
      color: this.pick(),
      life: maxLife * (0.2 + Math.random() * 0.8),
      maxLife,
      rotation: Math.random() * Math.PI,
      rotationSpeed: (Math.random() - 0.5) * 1.4,
      phase: Math.random() * Math.PI * 2,
    };
  }

  private makeBubble(width: number, height: number, anywhere: boolean): Particle {
    return {
      x: Math.random() * width,
      y: anywhere ? Math.random() * height : height + 8,
      vx: 0,
      vy: -(14 + Math.random() * 26),
      size: 3 + Math.random() * 7,
      color: this.pick(),
      life: Infinity,
      maxLife: Infinity,
      rotation: 0,
      rotationSpeed: 0,
      phase: Math.random() * Math.PI * 2,
    };
  }

  // ── Animation loop ────────────────────────────────────────────────────────

  private tick(time: number): void {
    const canvas = this.canvasRef?.nativeElement;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) {
      return;
    }
    // Clamp dt: a backgrounded tab hands back a huge delta that would teleport everything.
    const dt = this.lastTime ? Math.min(0.05, (time - this.lastTime) / 1000) : 0.016;
    this.lastTime = time;
    const { width, height } = this.size();

    ctx.clearRect(0, 0, width, height);

    const alive: Particle[] = [];
    for (const particle of this.particles) {
      this.step(particle, dt, width, height);
      if (particle.life > 0) {
        this.draw(ctx, particle);
        alive.push(particle);
      } else if (particle.isRocket) {
        alive.push(...this.explode(particle));
      }
    }
    this.particles = alive;

    // Burst effects end; ambient ones replenish (handled in step()). Replay bursts while
    // looping (simulator) or, for fireworks at FESTIVE, chain a couple of volleys.
    if (!this.isAmbient && this.particles.length === 0) {
      if (this.loop) {
        this.replayIn += dt;
        if (this.replayIn >= 1.1) {
          this.replayIn = 0;
          this.spawnInitial();
        }
      } else {
        this.rafId = null;
        this.clear();
        return; // one-shot finished — stop burning frames under an idle popup
      }
    }

    this.rafId = requestAnimationFrame((t) => this.tick(t));
  }

  private step(particle: Particle, dt: number, width: number, height: number): void {
    particle.phase += dt;
    switch (this.effectType) {
      case "CONFETTI": {
        particle.vy += 360 * dt; // gravity
        particle.vx *= 1 - 1.6 * dt; // drag
        particle.x += particle.vx * dt + Math.sin(particle.phase * 6) * 18 * dt;
        particle.y += particle.vy * dt;
        particle.rotation += particle.rotationSpeed * dt;
        particle.life -= dt;
        if (particle.y > height + 20) particle.life = 0;
        break;
      }
      case "FIREWORKS": {
        if (!particle.isRocket) {
          particle.vy += 220 * dt;
          particle.vx *= 1 - 1.2 * dt;
        }
        particle.x += particle.vx * dt;
        particle.y += particle.vy * dt;
        particle.life -= dt;
        break;
      }
      case "SNOW": {
        particle.x += Math.sin(particle.phase * 1.4) * 14 * dt + particle.vx * dt;
        particle.y += particle.vy * dt;
        if (particle.y > height + 6) {
          Object.assign(particle, this.makeSnow(width, height, false));
        }
        break;
      }
      case "SPARKLES": {
        particle.x += particle.vx * dt;
        particle.y += particle.vy * dt;
        particle.rotation += particle.rotationSpeed * dt;
        particle.life -= dt;
        if (particle.life <= 0) {
          Object.assign(particle, this.makeSparkle(width, height));
        }
        break;
      }
      case "BUBBLES": {
        particle.x += Math.sin(particle.phase * 2) * 16 * dt;
        particle.y += particle.vy * dt;
        if (particle.y < -10) {
          Object.assign(particle, this.makeBubble(width, height, false));
        }
        break;
      }
    }
  }

  private draw(ctx: CanvasRenderingContext2D, particle: Particle): void {
    switch (this.effectType) {
      case "CONFETTI": {
        // Fade only near the end of life so the fall stays vivid.
        ctx.globalAlpha = Math.max(0, Math.min(1, particle.life / 0.6));
        ctx.save();
        ctx.translate(particle.x, particle.y);
        ctx.rotate(particle.rotation);
        // Vertical squash fakes a ribbon tumbling in 3D.
        const squash = 0.35 + Math.abs(Math.sin(particle.phase * 5)) * 0.65;
        ctx.fillStyle = particle.color;
        ctx.fillRect(-particle.size / 2, (-particle.size * squash) / 2, particle.size, particle.size * squash);
        ctx.restore();
        break;
      }
      case "FIREWORKS": {
        ctx.globalAlpha = particle.isRocket ? 0.9 : Math.max(0, particle.life / particle.maxLife);
        ctx.fillStyle = particle.color;
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fill();
        break;
      }
      case "SNOW": {
        ctx.globalAlpha = 0.5 + Math.sin(particle.phase * 2) * 0.2;
        ctx.fillStyle = particle.color;
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fill();
        break;
      }
      case "SPARKLES": {
        // Twinkle: brightest mid-life, gone at both ends.
        const t = 1 - Math.abs(1 - (2 * (particle.maxLife - particle.life)) / particle.maxLife);
        ctx.globalAlpha = Math.max(0, Math.min(1, t));
        ctx.save();
        ctx.translate(particle.x, particle.y);
        ctx.rotate(particle.rotation);
        ctx.fillStyle = particle.color;
        const r = particle.size * (0.6 + t * 0.4);
        // Four-point star: two thin crossing diamonds.
        ctx.beginPath();
        ctx.moveTo(0, -r);
        ctx.quadraticCurveTo(0, 0, r, 0);
        ctx.quadraticCurveTo(0, 0, 0, r);
        ctx.quadraticCurveTo(0, 0, -r, 0);
        ctx.quadraticCurveTo(0, 0, 0, -r);
        ctx.fill();
        ctx.restore();
        break;
      }
      case "BUBBLES": {
        ctx.globalAlpha = 0.35;
        ctx.strokeStyle = particle.color;
        ctx.lineWidth = 1.4;
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.stroke();
        // Small highlight so it reads as a bubble, not a ring.
        ctx.globalAlpha = 0.5;
        ctx.fillStyle = particle.color;
        ctx.beginPath();
        ctx.arc(particle.x - particle.size * 0.35, particle.y - particle.size * 0.35, particle.size * 0.18, 0, Math.PI * 2);
        ctx.fill();
        break;
      }
    }
    ctx.globalAlpha = 1;
  }
}
